import {
  clipOverlapsAny,
  getClipDeltaBounds,
  isTimeInsideExistingClip,
  getNextClipStartBoundary,
  nextLocalEntityId,
  CLIP_COLORS,
  getDefaultClipName,
  CLIP_CREATION_EPSILON
} from '~/utils/video-editor/clip-utils.js';

export function useVideoEditorStoreClips({
  clips,
  clipNumber,
  file,
  setActiveEditClip,
  clearActiveEditClip,
  activeEditClipId,
  refreshClipMarkerSnapshots
}) {

  function resetClipNumberIfEmpty() {
    if (!clips.value.length) {
      clipNumber.value = 0;
    }
  }

  function remove({clip}) {
    clips.value = clips.value.filter(c => c.id !== clip.id);
    resetClipNumberIfEmpty();

    if (String(activeEditClipId.value) === String(clip?.id)) {
      clearActiveEditClip();
    }
  }

  function createClipAtTime({startTime, maxTime, refreshSnapshots = true, select = true} = {}) {
    const normalizedStartTime = Number(startTime);
    const normalizedMaxTime = Number(maxTime);

    if (
      !Number.isFinite(normalizedStartTime)
      || !Number.isFinite(normalizedMaxTime)
      || normalizedMaxTime <= 0
    ) {
      return null;
    }

    const clampedStartTime = Math.max(0, Math.min(normalizedStartTime, normalizedMaxTime));
    if (isTimeInsideExistingClip(clampedStartTime, clips.value)) {
      return null;
    }

    const desiredDuration = Math.max(
      normalizedMaxTime * VIDEO_EDITOR_INITIAL_CLIP_DURATION_RATIO,
      VIDEO_EDITOR_MIN_CLIP_DURATION_SECONDS
    );
    const endBoundary = getNextClipStartBoundary({
      time: clampedStartTime,
      clips: clips.value,
      maxTime: normalizedMaxTime
    });
    const endTime = Math.min(clampedStartTime + desiredDuration, endBoundary, normalizedMaxTime);

    if ((endTime - clampedStartTime) <= CLIP_CREATION_EPSILON) {
      return null;
    }

    const clipIdx = clipNumber.value++;
    const displayNumber = clipIdx + 1;
    const newClip = {
      id: nextLocalEntityId('clip'),
      clipNumber: clipIdx,
      color: CLIP_COLORS[clipNumber.value % CLIP_COLORS.length],
      name: getDefaultClipName({file: file.value, clipNumber: displayNumber}),
      markers: [
        {id: nextLocalEntityId('marker'), time: clampedStartTime, snapshot: null},
        {id: nextLocalEntityId('marker'), time: endTime, snapshot: null}
      ],
      frames: {}
    };

    if (clipOverlapsAny({clip: newClip, clips: clips.value})) {
      clipNumber.value = clipIdx;
      useErrorToast().add({
        title: 'Clip Overlap Not Allowed',
        description: 'Move the clip start or end so it does not overlap another clip.'
      });
      return null;
    }

    clips.value.push(newClip);
    const reactiveClip = clips.value[clips.value.length - 1];

    if (select) {
      setActiveEditClip({clipId: reactiveClip.id});
    }

    if (refreshSnapshots) {
      void refreshClipMarkerSnapshots({clip: reactiveClip});
    }

    return reactiveClip;
  }

  function createClip({startTime, endTime, name = null, refreshSnapshots = true, select = true} = {}) {
    const normalizedStart = Number(startTime);
    const normalizedEnd = Number(endTime);

    if (!Number.isFinite(normalizedStart) || !Number.isFinite(normalizedEnd) || normalizedEnd <= normalizedStart) {
      return null;
    }

    const clipIdx = clipNumber.value++;
    const displayNumber = clipIdx + 1;
    const newClip = {
      id: nextLocalEntityId('clip'),
      clipNumber: clipIdx,
      color: CLIP_COLORS[clipNumber.value % CLIP_COLORS.length],
      name: (name ?? getDefaultClipName({file: file.value, clipNumber: displayNumber})).slice(0, VIDEO_EDITOR_CLIP_NAME_MAX_LENGTH),
      markers: [
        {id: nextLocalEntityId('marker'), time: normalizedStart, snapshot: null},
        {id: nextLocalEntityId('marker'), time: normalizedEnd, snapshot: null}
      ],
      frames: {}
    };

    if (clipOverlapsAny({clip: newClip, clips: clips.value})) {
      clipNumber.value = clipIdx;
      useErrorToast().add({
        title: 'Clip Overlap Not Allowed',
        description: 'Move the clip start or end so it does not overlap another clip.'
      });
      return null;
    }

    clips.value.push(newClip);
    const reactiveClip = clips.value[clips.value.length - 1];

    if (select) {
      setActiveEditClip({clipId: reactiveClip.id});
    }

    if (refreshSnapshots) {
      void refreshClipMarkerSnapshots({clip: reactiveClip});
    }

    return reactiveClip;
  }

  function moveClip({clip, delta = 0, minTime = 0, maxTime = Infinity, refreshSnapshots = true}) {
    const nextDelta = Number(delta);
    if (!Number.isFinite(nextDelta) || nextDelta === 0) {
      return 0;
    }

    const ownerClip = clips.value.find(item => item === clip || item?.id === clip?.id);
    if (!ownerClip) {
      return 0;
    }

    const bounds = getClipDeltaBounds({
      clip: ownerClip,
      clips: clips.value,
      minTime,
      maxTime
    });

    if (!bounds) {
      return 0;
    }

    const clampedDelta = Math.min(Math.max(nextDelta, bounds.minDelta), bounds.maxDelta);
    if (clampedDelta === 0) {
      return 0;
    }

    const nextMarkers = (ownerClip.markers || []).map(marker => ({
      ...marker,
      time: marker.time + clampedDelta
    }));

    if (clipOverlapsAny({
      clip: {...ownerClip, markers: nextMarkers},
      clips: clips.value,
      excludeClipId: ownerClip.id ?? null,
      excludeClip: ownerClip
    })) {
      return 0;
    }

    for (const marker of ownerClip.markers || []) {
      marker.time += clampedDelta;
    }

    if (refreshSnapshots) {
      void refreshClipMarkerSnapshots({clip: ownerClip});
    }

    return clampedDelta;
  }

  function updateCLip({clip, updates}) {
    const nextUpdates = {...updates};
    if (typeof nextUpdates.name === 'string') {
      nextUpdates.name = nextUpdates.name.slice(0, VIDEO_EDITOR_CLIP_NAME_MAX_LENGTH);
    }
    Object.assign(clip, nextUpdates);
  }

  function addCustomFrame({clip, customFrame}) {
    if (!clip.frames) {
      clip.frames = {};
    }

    clip.frames.title = {
      design_project_id: customFrame.design_project_id,
      duration: customFrame.duration,
      s3_path: customFrame.s3_path || null,
      thumbnail: clip.frames.title?.thumbnail || customFrame.thumbnail || null
    };
  }

  function removeCustomFrame({clip, customFrame = null, designProjectId = null}) {
    if (!clip?.frames) {
      return false;
    }

    const normalizedDesignProjectId = designProjectId ?? customFrame?.design_project_id ?? null;
    const titleFrame = clip.frames.title;

    if (
      titleFrame
      && (
        normalizedDesignProjectId == null
        || String(titleFrame.design_project_id) === String(normalizedDesignProjectId)
      )
    ) {
      delete clip.frames.title;
      return true;
    }

    return false;
  }

  return {
    remove,
    createClip,
    createClipAtTime,
    moveClip,
    updateCLip,
    addCustomFrame,
    removeCustomFrame
  };
}
